import NewsCard from "@components/news/NewsCard"
import { AstroTransitionLink } from "@components/ui/AstroTransitionLink"
import { AstroNewsViewTransitions } from "@components/ui/AstroViewTransitions"
import { BigHeading, PageLayout } from "@components/ui/PageLayout"
import { useRoutePageContext } from "@components/ui/route-page-context"
import { newsListPageQueryOptions } from "@data/public-page-data-query"
import { newsTransitionName } from "@lib/view-transitions"
import * as m from "@paraglide/messages"
import { utcDateOnly } from "@playbrain/config"
import type { NewsItem } from "@site-types/content"
import type { NewsListPageData } from "@site-types/public-page-data"
import { createFileRoute } from "@tanstack/react-router"
import { localizedRouteHead } from "@utils/route-head"
import { getLocalizedNews } from "@utils/site-data"

export const Route = createFileRoute("/$locale/news/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(newsListPageQueryOptions(params.locale as "en" | "ja")),
  head: ({ params }) => {
    const locale = params.locale as "en" | "ja"
    return localizedRouteHead({ locale, page: "news", path: `/${locale}/news` })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const data = Route.useLoaderData()
  return (
    <PageLayout>
      <NewsListPage data={data} />
    </PageLayout>
  )
}

const featuredNewsCount = 5

export function NewsListPage({ data }: { data: NewsListPageData }) {
  const { locale } = useRoutePageContext()
  const news = getLocalizedNews(data, locale)
  const featuredNews = news.slice(0, featuredNewsCount)
  const archiveNews = news.slice(featuredNewsCount)

  return (
    <>
      <AstroNewsViewTransitions news={news} />
      <BigHeading variant="h1">
        {(m.news_post_heading({}, { locale: locale }) || "News").toUpperCase()}
      </BigHeading>
      <div
        className="mx-auto max-w-[var(--max-width-column)] pb-[3rem] max-page:mt-[calc(-5rem+max(-40px,calc(-1*2vw)))] max-page:mr-[-2rem] max-page:ml-[-2rem] max-page:w-[calc(100%+4rem)]"
        data-testid="news-list-container"
      >
        <ul
          className="relative z-[5] m-[max(-40px,calc(-1*2vw))_30px_30px] grid grid-cols-1 news-wide:grid-cols-7 news:grid-cols-5 gap-[20px] pb-0"
          data-testid="news-list-featured"
        >
          {featuredNews.map((post, index) => (
            <NewsListItem
              index={index}
              key={`${locale}-${post.slug?.current ?? "news"}-${index}`}
              post={post}
            />
          ))}
          {news.length === 0 ? <h5>No Records Found</h5> : null}
        </ul>
        {archiveNews.length > 0 ? (
          <ul
            aria-label="News archive"
            className="relative z-[5] mt-[20px] mr-[30px] mb-[30px] ml-[30px] grid grid-cols-1 news-wide:grid-cols-7 news:grid-cols-5 gap-[20px] pb-[50px]"
            data-testid="news-list-archive"
          >
            {archiveNews.map((post, archiveIndex) => {
              const index = featuredNewsCount + archiveIndex
              return (
                <NewsListItem
                  isArchive
                  index={index}
                  key={`${locale}-${post.slug?.current ?? "news"}-${index}`}
                  post={post}
                />
              )
            })}
          </ul>
        ) : null}
      </div>
    </>
  )
}

function NewsListItem({
  isArchive = false,
  index,
  post,
}: {
  isArchive?: boolean
  index: number
  post: NewsItem
}) {
  const { locale } = useRoutePageContext()
  const slug = post.slug?.current ?? ""
  const transitionName = newsTransitionName(slug)
  const displayDate = post.publishDate ? (utcDateOnly(post.publishDate) ?? "") : ""
  return (
    <li
      className={[
        "grid h-auto list-none content-start gap-x-[var(--gap)] p-0 text-[1rem] [--body-width:calc(100%-(var(--image-width)+var(--gap)))] [--gap:10px] [--image-width:250px] [container-type:inline-size] [grid-template:auto_auto_auto_auto_/_var(--image-width)_1fr]",
        index === 0
          ? "news-wide:col-[1/span_3] news:col-[1/span_3] news-wide:row-[1/span_2] news:row-[1/span_2] news:[&_[data-news-card-blurb]]:text-[2.5em] news:[&_[data-news-card-date]]:text-[2.5em] news:[&_h4]:text-[3em]"
          : "",
        index === 1 ? "news-wide:col-[4/span_2] news:col-[4/span_2]" : "",
        index === 2 ? "news-wide:col-[6/span_2] news:col-[4/span_2]" : "",
        index === 3 ? "news-wide:col-[4/span_2] news:col-[1/span_5]" : "",
        index === 4 ? "news-wide:col-[6/span_2] news:col-[1/span_5]" : "",
        isArchive ? "news-wide:col-[1/span_7] news:col-[1/span_5]" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-id={`news-card-${slug || index}`}
      data-index={index}
      data-archive={isArchive ? "true" : undefined}
      key={`${locale}-${slug || "news"}-${index}`}
    >
      <AstroTransitionLink
        to={`/${locale}/news/${slug}`}
        aria-label={post.title || "Open news article"}
        className="block h-auto self-start overflow-hidden [grid-row:1/span_4] hover:outline hover:outline-2 hover:outline-[var(--white)] [@container(max-width:600px)]:col-[1/span_2] [@container(max-width:600px)]:row-[1/span_1] [@container(max-width:600px)]:[--image-width:100%]"
        data-id={`news-card-image-link-${slug || index}`}
        transitionName={transitionName}
      >
        <NewsCard
          post={post}
          priority={index === 0}
        />
      </AstroTransitionLink>
      <h4 className="block pb-[1em] leading-[1.8em] [@container(max-width:600px)]:col-[1/span_2] [@container(max-width:600px)]:mt-[1em]">
        <AstroTransitionLink
          to={`/${locale}/news/${slug}`}
          className="mt-0 inline-block text-[1.2em] text-[var(--white)] [text-wrap:pretty] visited:text-[var(--white)] hover:text-[var(--white)]"
          data-id={`news-card-title-link-${slug || index}`}
          transitionName={transitionName}
        >
          {post.title}
        </AstroTransitionLink>
      </h4>
      <p
        className="m-0 inline text-[1.4em] text-[var(--white)] italic [text-wrap:pretty] [@container(max-width:600px)]:col-[1/span_2]"
        data-news-card-blurb
      >
        {post.newsCoverBlurb}
      </p>
      <p
        className="mt-[0.3em] mr-0 mb-0 ml-0 text-[1.4em] text-[var(--white-50)] italic [text-wrap:pretty] [vertical-align:middle] [@container(max-width:600px)]:col-[1/span_2]"
        data-news-card-date
      >
        {displayDate}
      </p>
    </li>
  )
}
